import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {Gear, DividedLanes, Book, Bell} from '@cfa-icons/brand';
import {useOktaAuth} from '@okta/okta-react';
import {SideNavigation, SideNavigationContent, NavLink, Logo, Icon, Avatar, Accordion, AccordionItem, Tooltip} from 'cfa-react-components';
import { NavLink as ReactRouterNavLink } from 'react-router-dom';

import './Sidebar.scss';
import {IOktaUser} from 'interfaces';
import { UserService } from 'services';
import { UserRole } from 'enums';

export function Sidebar() {

    const {oktaAuth, authState} = useOktaAuth();
    const [user, setUser] = useState<IOktaUser>();

    // keep track if user was previously authenticated on last render
    const prevIsAuthedRef = useRef(false);

    useEffect(() => {
        if (!prevIsAuthedRef.current && authState.isAuthenticated) {
            // load user into navbar
            oktaAuth.token.getUserInfo()
                .then((loadedUser: IOktaUser) => {
                    setUser(loadedUser);
                })
                .catch((err) => {
                    console.log('Error fetching user: ', err);
                });
        } else if (prevIsAuthedRef.current && !authState.isAuthenticated) {
            // clear cached user
            setUser(undefined);
        }

        prevIsAuthedRef.current = authState.isAuthenticated;
    }, [user, authState]);

    const signout = () => {
        UserService.reset();
        oktaAuth.signOut();
    }

    const avatarSignout: ReactNode = (<>
        {user && (<>
            <Tooltip
                content={user.name}
                placement='bottom-end'
                showOnElementEvents={[
                    'hover'
                ]}
            >
                <Avatar name={user.name} />
            </Tooltip>
        </>)}
    </>);

    return (
        <SideNavigation>
            <SideNavigationContent
                section="start"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '16px',
                }}
            >
                <Logo logo="symbolInCircle" size="md" />
            </ SideNavigationContent>
            {UserService.oktaUserInfo.role >= UserRole.ViewOnly && <SideNavigationContent
                section="middle"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <NavLink
                    to="/home" 
                    as={ReactRouterNavLink}
                    icon={<Icon icon={DividedLanes}/>}
                    variant="sidenavigation-link"
                >
                    Home
                </NavLink>
                {UserService.oktaUserInfo.role == UserRole.Admin && <NavLink 
                    to="/config"
                    as={ReactRouterNavLink}
                    icon={<Icon icon={Gear}/>}
                    variant="sidenavigation-link"
                >
                    Config
                </NavLink>}
                <NavLink 
                    href='https://cfacorp.atlassian.net/wiki/spaces/SEEP/pages/4657611171/MQTT+Capture-Replay+User+Guide'
                    icon={<Icon icon={Book}/>}
                    variant="sidenavigation-link"
                    target='_blank'
                >
                    User Guide
                </NavLink>
                {UserService.oktaUserInfo.role == UserRole.Admin && <NavLink 
                    href='https://pinger.cfasmartequip.cfahome.com/'
                    icon={<Icon icon={Bell}/>}
                    variant="sidenavigation-link"
                    target='_blank'
                >
                    Pinger
                </NavLink>}
            </ SideNavigationContent>}
            <SideNavigationContent
                section="end"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {user && (<>
                    <Accordion
                        iconPosition="end"
                        variant="default"
                        style={{ marginBottom: 4, marginTop: 32 }} 
                    >
                        <AccordionItem header={avatarSignout} itemKey="0">
                            <NavLink onClick={signout} variant="sidenavigation-link">
                                Log Out
                            </NavLink>
                        </AccordionItem>
                    </Accordion>
                    
                </>)}
            </SideNavigationContent>
        </SideNavigation>
    );
};